import { reduceConversation } from '../conversationReducer';
import { ConversationDialogueState, CONVERSATION_STATE_TRANSITIONS, ConversationTrigger, getNextConversationState, getConversationStateMetadata } from '../../DialogueManagementConversation/ConversationDialogueBlueprint';
import { DialogueContext, DialogueEvent } from '../types';
import { TASK_STATE_TRANSITIONS, TaskDialogueState, TaskTrigger, getNextTaskState } from '../../DialogueManagementTaskClassification/TaskClassificationDialogueBlueprint';

export type TestResult = {
  scope: 'conversation' | 'task';
  fromState: string;
  trigger: string;
  expected: string | null;
  actual: string | undefined | null;
  effects: number;
  passed: boolean;
  message?: string;
};

const baseContext: DialogueContext = {
  slots: {},
  language: 'en',
  userId: 'auto',
  conversationId: 'auto-1'
};

function makeParseResult(intentId: string): DialogueEvent {
  return { type: 'PARSE_RESULT', messageId: 'auto_msg', intentId, slots: {}, confidence: 1 } as DialogueEvent;
}

export async function runAutoTests(): Promise<{ results: TestResult[]; failures: TestResult[]; summary: string }>{
  const results: TestResult[] = [];

  // Conversation tests
  for (const [state, transitions] of Object.entries(CONVERSATION_STATE_TRANSITIONS)) {
    const fromState = state as ConversationDialogueState;
    for (const [trigger, toState] of Object.entries(transitions)) {
      const intent = trigger as ConversationTrigger;
      const event = makeParseResult(intent);
      const ctx: DialogueContext = { ...baseContext, awaitingParseId: 'auto_msg' };
      const res = reduceConversation(fromState, ctx, event);
      const expected = getNextConversationState(fromState, intent);
      const actual = res.nextState;
      const effectsCount = res.effects.length;
      const hasEntryEffect = expected ? !!getConversationStateMetadata(expected).entryPromptKey : false;
      const passed = (!!expected && actual === expected) && (effectsCount > 0 || hasEntryEffect);
      results.push({ scope: 'conversation', fromState, trigger: intent, expected: expected || null, actual, effects: effectsCount, passed, message: passed ? undefined : 'Missing transition or effects' });
    }
  }

  // Task tests (under conv_task_processing)
  for (const [state, transitions] of Object.entries(TASK_STATE_TRANSITIONS)) {
    const fromTask = state as TaskDialogueState;
    for (const [trigger, toTask] of Object.entries(transitions)) {
      const t = trigger as TaskTrigger;
      const event = makeParseResult(t);
      const ctx: DialogueContext = { ...baseContext, awaitingParseId: 'auto_msg', taskState: fromTask, taskContext: { slots: {} } };
      const res = reduceConversation('conv_task_processing', ctx, event);
      const expectedTask = getNextTaskState(fromTask, t);
      const actualTask = res.nextContext.taskState;
      const effectsCount = res.effects.length;
      const passed = expectedTask
        ? (expectedTask === 'task_completed' || expectedTask === 'task_aborted'
            ? actualTask === undefined // terminal clears task state
            : actualTask === expectedTask) && effectsCount > 0
        : false;
      results.push({ scope: 'task', fromState: fromTask, trigger: t, expected: expectedTask || null, actual: actualTask || null, effects: effectsCount, passed, message: passed ? undefined : 'Missing task transition or effects' });
    }
  }

  const total = results.length;
  const passed = results.filter(r => r.passed).length;
  const failures = results.filter(r => !r.passed);
  const summary = `Auto Tests: ${passed}/${total} passed | Failures: ${failures.length}`;
  return { results, failures, summary };
}


